import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Plus, Download, MagnifyingGlass, User, Phone, CurrencyInr } from '@phosphor-icons/react';

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

export function CustomersList() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [execFilter, setExecFilter] = useState('');
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCust, setNewCust] = useState({ mobile: '', name: '', address: '', executive_name: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (execFilter) params.append('executive_name', execFilter);
      const { data } = await api.get(`/api/admin/customers?${params.toString()}`);
      setCustomers(data.customers || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [search, execFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/api/masters/executives').then(r => setExecutives(r.data || [])).catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!newCust.mobile || !newCust.name) {
      toast.error('Mobile and Name are required');
      return;
    }
    try {
      const { data } = await api.post('/api/admin/customers', newCust);
      toast.success(data.created ? 'Customer created' : 'Customer updated');
      setShowCreate(false);
      setNewCust({ mobile: '', name: '', address: '', executive_name: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save customer');
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get('/api/admin/customers-export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download');
    }
  };

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">Customers</h1>
            <p className="text-sm text-zinc-500 mt-2">Mobile-keyed customer master — auto-synced from job orders</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="customers-export-btn" variant="outline" onClick={downloadCsv}>
              <Download size={18} className="mr-2" /> Export CSV
            </Button>
            <Button data-testid="customers-new-btn" onClick={() => setShowCreate(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
              <Plus size={18} className="mr-2" /> New Customer
            </Button>
          </div>
        </div>

        <Card className="border-zinc-300 rounded-sm">
          <CardContent className="p-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Search</Label>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  data-testid="customers-search-input"
                  className="pl-9"
                  placeholder="Name or mobile..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="min-w-[200px]">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Executive</Label>
              <select
                data-testid="customers-exec-filter"
                className="w-full h-10 px-3 border border-zinc-300 rounded-sm bg-white"
                value={execFilter}
                onChange={(e) => setExecFilter(e.target.value)}
              >
                <option value="">All Executives</option>
                {executives.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div className="text-sm text-zinc-500">
              Total: <span className="font-bold text-zinc-950">{total}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-300 rounded-sm">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-300">
                <tr>
                  <th className="text-left p-3 font-bold text-zinc-700 uppercase text-xs tracking-wider">Mobile</th>
                  <th className="text-left p-3 font-bold text-zinc-700 uppercase text-xs tracking-wider">Name</th>
                  <th className="text-left p-3 font-bold text-zinc-700 uppercase text-xs tracking-wider">Executive</th>
                  <th className="text-right p-3 font-bold text-zinc-700 uppercase text-xs tracking-wider">JOs</th>
                  <th className="text-right p-3 font-bold text-zinc-700 uppercase text-xs tracking-wider">Order Value</th>
                  <th className="text-right p-3 font-bold text-zinc-700 uppercase text-xs tracking-wider">Paid</th>
                  <th className="text-right p-3 font-bold text-zinc-700 uppercase text-xs tracking-wider">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="p-6 text-center text-zinc-400">Loading...</td></tr>
                ) : customers.length === 0 ? (
                  <tr><td colSpan={7} className="p-6 text-center text-zinc-400">No customers found</td></tr>
                ) : customers.map((c) => (
                  <tr
                    key={c.mobile}
                    data-testid={`customer-row-${c.mobile}`}
                    onClick={() => navigate(`/customers/${c.mobile}`)}
                    className="border-b border-zinc-200 hover:bg-orange-50 cursor-pointer"
                  >
                    <td className="p-3 font-mono text-zinc-950">{c.mobile}</td>
                    <td className="p-3 text-zinc-950 font-bold">{c.name || '—'}</td>
                    <td className="p-3 text-zinc-600">{c.executive_name || '—'}</td>
                    <td className="p-3 text-right">
                      <Badge variant="secondary">{c.jo_count}</Badge>
                    </td>
                    <td className="p-3 text-right text-zinc-950">{fmtINR(c.total_order_value)}</td>
                    <td className="p-3 text-right text-emerald-700">{fmtINR(c.total_paid)}</td>
                    <td className={`p-3 text-right font-bold ${c.outstanding > 0 ? 'text-red-600' : 'text-zinc-500'}`}>
                      {fmtINR(c.outstanding)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Mobile Number *</Label>
              <Input
                data-testid="new-customer-mobile"
                value={newCust.mobile}
                onChange={(e) => setNewCust({ ...newCust, mobile: e.target.value })}
                placeholder="10-digit mobile"
              />
            </div>
            <div>
              <Label>Customer Name *</Label>
              <Input
                data-testid="new-customer-name"
                value={newCust.name}
                onChange={(e) => setNewCust({ ...newCust, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                data-testid="new-customer-address"
                value={newCust.address}
                onChange={(e) => setNewCust({ ...newCust, address: e.target.value })}
              />
            </div>
            <div>
              <Label>Executive</Label>
              <select
                data-testid="new-customer-exec"
                className="w-full h-10 px-3 border border-zinc-300 rounded-sm bg-white"
                value={newCust.executive_name}
                onChange={(e) => setNewCust({ ...newCust, executive_name: e.target.value })}
              >
                <option value="">Select Executive</option>
                {executives.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="save-customer-btn" onClick={handleCreate} className="bg-orange-500 hover:bg-orange-600 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
